import { z } from 'zod';
import { isAddress, type Address } from 'viem';
import { ADDRESSES_SENT_TXS as localStorageKey } from '../KEYS';
import { TX_KIND_EVENT, type PendingTx } from '@/shared/utils/txKinds';

// Persisted pending-tx list keyed by user address. Re-read from storage on
// every get so multiple tabs don't diverge — a module-init cache would only
// see the state at startup. Every row is parsed on read: a row this app did
// not write in its current shape (an older build, a hand edit) is dropped
// alone, never trusted into the tracker and never poisoning its siblings.

// Kept as written (a checksum re-derivation would change the strings the
// tracker's predicates compare with ===).
const address = z.custom<Address>((v) =>
  isAddress(String(v), { strict: false }),
);

const DetailsSchema = z.discriminatedUnion('kind', [
  z.looseObject({
    kind: z.literal('sell_subscription'),
    issuerAddress: address,
    issuerUsername: z.string(),
    inputValue: z.string(),
    iOwnAmount: z.number(),
  }),
  z.looseObject({ kind: z.literal('claim_san') }),
  z.looseObject({ kind: z.literal('claim_points') }),
  z.looseObject({ kind: z.literal('claim_legacy_san') }),
  z.looseObject({ kind: z.literal('withdraw_zk_fees') }),
  z.looseObject({ kind: z.literal('stake_approve'), kolAddress: address }),
  z.looseObject({
    kind: z.literal('stake_deposit'),
    kolAddress: address,
    amount: z.string(),
    priorActiveStake: z.string(),
  }),
  z.looseObject({
    kind: z.literal('stake_withdraw'),
    stakeId: z.number(),
    kolAddress: address,
    amount: z.string(),
    priorActiveStake: z.string(),
    stakeInactive: z.boolean().optional(),
  }),
]);

const PendingTxSchema = z.looseObject({
  hash: z.string(),
  networkIdHex: z.string(),
  details: DetailsSchema,
  event: z.enum(Object.values(TX_KIND_EVENT)),
  trackProps: z.record(z.string(), z.unknown()),
  tracked: z.boolean().optional(),
  submittedAt: z.number().optional(),
});

const StoreSchema = z.record(z.string(), z.unknown());
const RowsSchema = z.array(z.unknown());

type Store = Record<string, PendingTx[]>;

function toPendingTx(row: z.output<typeof PendingTxSchema>): PendingTx {
  const { hash, networkIdHex, details, event, trackProps } = row;
  return {
    hash,
    networkIdHex,
    details,
    event,
    trackProps,
    ...(row.tracked === undefined ? {} : { tracked: row.tracked }),
    ...(row.submittedAt === undefined ? {} : { submittedAt: row.submittedAt }),
  };
}

function read(): Store {
  const raw = localStorage.getItem(localStorageKey);
  // Stryker disable next-line ConditionalExpression: equivalent — a null row would go through JSON.parse as "null" and be rejected by the store schema into the same {}; this line is the fast path and the TS narrowing.
  if (raw === null) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Swallowed on purpose: an unparsed row leaves `parsed` undefined, which
    // the store schema below rejects into the same empty store.
  }
  const store = StoreSchema.safeParse(parsed);
  if (!store.success) return {};
  const out: Store = {};
  for (const [key, value] of Object.entries(store.data)) {
    const rows = RowsSchema.safeParse(value);
    if (!rows.success) continue;
    out[key] = rows.data.flatMap((row) => {
      const tx = PendingTxSchema.safeParse(row);
      return tx.success ? [toPendingTx(tx.data)] : [];
    });
  }
  return out;
}

function write(store: Store) {
  if (Object.keys(store).length === 0) {
    localStorage.removeItem(localStorageKey);
    return;
  }
  localStorage.setItem(
    localStorageKey,
    JSON.stringify(store, (_, value: unknown) =>
      typeof value === 'bigint' ? value.toString() : value,
    ),
  );
}

export default {
  get(address: Address): PendingTx[] {
    return read()[address] ?? [];
  },

  set(address: Address, sentTxs: PendingTx[]): void {
    const store = read();
    if (sentTxs.length === 0) {
      write(
        Object.fromEntries(
          Object.entries(store).filter(([key]) => key !== address),
        ),
      );
      return;
    }
    store[address] = sentTxs;
    write(store);
  },

  clear(): void {
    localStorage.removeItem(localStorageKey);
  },
};
